import { Badge, Col, Modal, ModalProps, Row, Typography } from "antd";
import React, { useMemo, useRef, useState } from "react";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import Header from "./Header";
import Footer from "./Footer";
import CustomTag from "./Tags/CustomTag";
import { BellFilled, SafetyCertificateOutlined, WifiOutlined } from "@ant-design/icons";
import CustomButton from "./CustomButton";
import SideInfoBar from "./SideInfoBar";
import DryingView from "./DryingView";
import RunFansView from "./RunFansView";
import MoveGrain from "./MoveGrainView";
import './HmiViewModal.css';
import AlertsModal from "./Alerts/AlertsModal";
import LimitsModal from "./Alerts/LimitsModal";
import { useGetPremierPlusAlertsQuery } from "src/queries/useGetPremierPlusAlertsQuery";
import FillLevelExplanationModal from "./FillLevelExplanationModal";
import AlertCategory from "src/consts/AlertCategory";
import EventCategory from "src/consts/EventCategory";

const { Text, Title } = Typography;

export enum ViewHmi{
  Dry=1,
  Fan=2,
  MoveGrain=3
}

interface HmiViewModalProps extends ModalProps {
  binDto: PremierPlusBinDTO | null;
  onClose: (result: any) => void;
  view: ViewHmi | null;
}

const HmiViewModal : React.FC<HmiViewModalProps> = (props) => {
  const [limitsModalOpen, setLimitsModalOpen] = useState(false);
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  

  // ⬅️ container that spans the whole parent modal content
  const parentContentRef = useRef<HTMLDivElement>(null);

  const handleClickLimits = () => setLimitsModalOpen(true);
  const handleCloseLimits = () => setLimitsModalOpen(false);
  const handleClickAlerts = () => setAlertsModalOpen(true);
  const handleCloseAlerts = () => setAlertsModalOpen(false);

  const handleClose = () => {
    handleCloseLimits();
    handleCloseAlerts();
    props.onClose(null);
  }

  const [showFillLevelModal, setShowFillLevelModal] = React.useState(false);  

  const handleShowFillLevelModal = () => {
    setShowFillLevelModal(true);
  }

  const handleCloseFillLevelModal = () => {
    setShowFillLevelModal(false);
  }

  const innerGamGrainDepth = useMemo(() => {
    return props.binDto?.grainHeightState_HeightAtPeak;
  },[props.binDto]);

  const outerGamGrainDepth = useMemo(() => {
    return props.binDto?.grainHeightState_HeightAtEave;
  },[props.binDto]);

  const totalVolumeBushels = useMemo (() => {
    return props.binDto?.grainHeightState_TotalVolumeBushels ?? null;
  }, [props.binDto]);

  const fillPercent = useMemo (() => {
    return props.binDto?.grainHeightState_PercentFull ?? null;
  }, [props.binDto]);

  const numLimitsTripped = useMemo (() => {
    return props.binDto?.activeAlerts?.filter(a => a.eventCategory === EventCategory.Limit && a.triggerTimeUtc != null && a.resolveTimeUtc == null).length;
  },[props.binDto]);

  const numAlertsTripped = useMemo (() => {
    return props.binDto?.activeAlerts?.filter(a => a.eventCategory !== EventCategory.Limit && a.triggerTimeUtc != null && a.resolveTimeUtc == null).length;
  },[props.binDto]);

  const rightSideHeaderTags = [
    <CustomTag key="cell" label="Cell: OK" color="green" icon={<WifiOutlined/>}/>,
    <Badge count={numLimitsTripped} offset={[-10, 5]}>
      <CustomButton 
        key="limits"
        icon={<SafetyCertificateOutlined />} 
        label={'LIMITS'} 
        height={30} 
        maxWidth={128}
        onClick={handleClickLimits}
      />
    </Badge>
    ,
    <Badge count={numAlertsTripped} offset={[-10, 5]}>
      <CustomButton 
        key="alerts"
        icon={<BellFilled />}  
        label={'ALERTS'} 
        height={30} 
        maxWidth={128}
        onClick={handleClickAlerts}
      />
    </Badge>

  ];

  // compute a safe zIndex for the child (default antd modal zIndex is ~1000)
  const parentZ = props.zIndex ?? 1000;
  const childZ = parentZ + 1;

  return(
    <Modal
      {...props} 
      title={
        <Row gutter={8} align={'middle'}>
          <Col>
            <Title level={5}>HMI View</Title>
          </Col>
          <Col>
            {' - '}
          </Col>
          <Col> 
            <Text style={{ color:'gray', marginBottom:'0px' }}>Remote visibility of the local touchscreen (HMI) is VIEW ONLY, so you will not be able to change any settings.</Text>
          </Col>
        </Row>
      }
      destroyOnClose
      centered
      footer={null}
      width='1076px'
      height='768px'
      onCancel={handleClose}
      rootClassName="hmi-parent-modal"
    >
      <div ref={parentContentRef} style={{ position: 'relative' }}>
        <Header binDto={props.binDto} rightSideItems={rightSideHeaderTags} />

        <Row>
          <Col span={18}>
            {props.view === ViewHmi.Fan && <RunFansView binDto={props.binDto}/>}
            {props.view === ViewHmi.Dry && <DryingView binDto={props.binDto}/>}
            {props.view === ViewHmi.MoveGrain && <MoveGrain binDto={props.binDto}/>}
          </Col>
          <Col span={6}>
            <SideInfoBar 
            innerGamGrainDepth={innerGamGrainDepth}
            outerGamGrainDepth={outerGamGrainDepth}
            onClickShowFillLevel={handleShowFillLevelModal}
            binDto={props.binDto} />
          </Col>
        </Row>

        <Footer binDto={props.binDto}/>

        <AlertsModal 
        binId={props.binDto?.binId ?? null}
        binDto={props.binDto}
        showAlertsModal={alertsModalOpen} 
        onClose={handleCloseAlerts}       
        parentRef={parentContentRef} 
        />

        <LimitsModal 
        binDto={props.binDto} 
        showLimitsModal={limitsModalOpen} 
        onClose={handleCloseLimits}        
        parentRef={parentContentRef}
        />

        <FillLevelExplanationModal 
        show={showFillLevelModal}
        onClose={handleCloseFillLevelModal}
        grainDepthAtCenter={innerGamGrainDepth}
        grainDepthAtPerimeter={outerGamGrainDepth}
        bushelsFull={totalVolumeBushels}
        percentFull={fillPercent}
        parentRef={parentContentRef}
        />

      </div>
    </Modal>
  );
};

export default HmiViewModal;
